// src/store.js
import {
  configureStore,
  createSlice,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { updateEvent } from "store/CalendarSlice";
import { axiosInstance } from "services/axios-supabase";
import { supabase } from "services/supabase";
import _ from "lodash";
import moment from "moment";

/** Same RFC3339 + offset shape as dashboard `GET /events` (api normalizes with User-Timezone). */
const toRFC3339WithOffset = (m) =>
  m.format("YYYY-MM-DD[T]HH:mm:ss.SSS") + m.format("Z");

// ------------------------------------------------------------------------
// Initial state
const initialState = {
  clients: [],
  client: null,
  clientReports: [],
  clientEvents: [],
  /** True while fetchSupabaseClientEvents is in flight (client list may still be empty). */
  clientEventsLoading: false,
  loading: true,
  error: null,
  clientReportsLoaded: false,
  clientsLoaded: false,
};

let lastRefresh = 0;
export const selectClientsLoaded = (state) => state.clientReducer.clientsLoaded;
export const selectClients = (state) => state.clientReducer.clients;

export const upgradeClientEvents = createAsyncThunk(
  "data/upgradeClientEvents",
  async ({ id, options }, { rejectWithValue }) => {
    console.log("upgradeClientEvents", id, options);
    try {
      const response = await axiosInstance.put(`/clients/${id}/upgrade`, {
        options: options,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSupabaseClients = createAsyncThunk(
  "data/fetchSupabaseClients",
  async (params = {}, { rejectWithValue }) => {
    try {
      // Throttle refreshes to avoid excessive requests
      if (lastRefresh > 0 && moment().unix() - lastRefresh < 3) {
        lastRefresh = 0;
        params.force = "yes";
      }

      // Build Supabase query
      let query = supabase.from("clients").select("*");
      // Order by last_utilized_at desc (nulls last), then name desc
      query = query.order("last_utilized_at", { ascending: false });
      query = query.order("name", { ascending: false });

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      lastRefresh = moment().unix();
      return data;
    } catch (error) {
      return rejectWithValue(error.message || error.toString());
    }
  }
);

export const checkSupabaseClientPrefix = createAsyncThunk(
  "data/checkSupabaseClientPrefix",
  async ({ userId, ...params }, { rejectWithValue }) => {
    try {
      if (!userId) {
        console.error("checkSupabaseClientPrefix: User not authenticated");
        throw new Error("User not authenticated");
      }

      // Check if client with this prefix exists
      const { data, error } = await supabase
        .from("clients")
        .select("id, name, prefix")
        .eq("uid", userId)
        .eq("prefix", params.prefix)
        .single();

      if (error && error.code !== "PGRST116") {
        // PGRST116 is "not found" which is expected when checking for uniqueness
        throw error;
      }

      return {
        exists: !!data,
        client: data || null,
      };
    } catch (error) {
      console.error("checkSupabaseClientPrefix: Caught error", error);
      return rejectWithValue(error.message);
    }
  }
);

export const checkClientPrefix = createAsyncThunk(
  "data/checkClientPrefix",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/clients/prefix", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createSupabaseClient = createAsyncThunk(
  "data/createSupabaseClient",
  async ({ userId, ...newClientData }, { rejectWithValue }) => {
    console.log(
      "createSupabaseClient: Starting creation with data:",
      newClientData
    );
    try {
      if (!userId) {
        console.error("createSupabaseClient: User not authenticated");
        throw new Error("User not authenticated");
      }

      // Validate required fields
      if (!newClientData.name) {
        throw new Error("Client name is required");
      }

      // Prepare client data
      const clientData = {
        ...newClientData,
        uid: userId, // Explicitly set uid for RLS policy
      };

      // Remove any empty or undefined fields
      Object.keys(clientData).forEach((key) => {
        if (clientData[key] === "" || clientData[key] === undefined) {
          delete clientData[key];
        }
      });

      console.log("createSupabaseClient: Prepared client data:", clientData);

      const { data: result, error } = await supabase
        .from("clients")
        .insert(clientData)
        .select()
        .single();

      if (error) {
        console.error("createSupabaseClient: Supabase error details:", {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        throw error;
      }

      console.log("createSupabaseClient: Client created successfully:", result);

      // Create log record for the new client (matching API behavior)
      try {
        const { error: logError } = await supabase
          .from("logs")
          .insert({
            uid: userId,
            description: `You have a new client called "${result.name}".`,
            category: "client",
            calendar_id: null,
            data: null,
            status: "unread",
          });

        if (logError) {
          console.error("createSupabaseClient: Error creating log record:", logError);
          // Don't throw - log creation failure shouldn't fail client creation
        }
      } catch (logError) {
        console.error("createSupabaseClient: Exception creating log record:", logError);
        // Don't throw - log creation failure shouldn't fail client creation
      }

      return result;
    } catch (error) {
      console.error("createSupabaseClient: Caught error", error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSupabaseClient = createAsyncThunk(
  "data/deleteSupabaseClient",
  async (clientId, { rejectWithValue, getState }) => {
    try {
      console.log("deleteSupabaseClient: Deleting client", clientId);

      // Get client name for logging (from state or fetch)
      let clientName = null;
      const state = getState();
      const client = state.clientReducer.clients?.find((c) => c.id === clientId);
      
      if (client) {
        clientName = client.name;
      } else {
        // If not in state, fetch it
        const { data: clientData } = await supabase
          .from("clients")
          .select("name")
          .eq("id", clientId)
          .single();
        
        if (clientData) {
          clientName = clientData.name;
        }
      }

      // Get current user ID
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;

      const { error } = await supabase
        .from("clients")
        .delete()
        .eq("id", clientId);

      if (error) {
        console.error("deleteSupabaseClient: Supabase error", error);
        throw error;
      }

      console.log("deleteSupabaseClient: Client deleted successfully");

      // Create log record for the deleted client
      if (userId && clientName) {
        try {
          await supabase.from("logs").insert({
            uid: userId,
            description: `Removed an existing client "${clientName}".`,
            category: "client",
            calendar_id: null,
            data: null,
            status: "unread",
          });
        } catch (logError) {
          console.error("deleteSupabaseClient: Error creating log record:", logError);
          // Don't throw - log creation failure shouldn't fail client deletion
        }
      }

      return clientId;
    } catch (error) {
      console.error("deleteSupabaseClient: Caught error", error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateSupabaseClient = createAsyncThunk(
  "data/updateSupabaseClient",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      console.log(
        "updateSupabaseClient: Updating client",
        id,
        "with data:",
        data
      );

      const updateData = {
        ...data,
        updated_at: moment().toISOString(),
      };

      const { data: result, error } = await supabase
        .from("clients")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("updateSupabaseClient: Supabase error", error);
        throw error;
      }

      console.log("updateSupabaseClient: Client updated successfully:", result);
      return result;
    } catch (error) {
      console.error("updateSupabaseClient: Caught error", error);
      return rejectWithValue(error.message);
    }
  }
);

// FOR REVIEW - does this do the same thing as eventMarkAsBilled?
export const eventMarkAsBilledSupabase = createAsyncThunk(
  "data/eventMarkAsBilledSupabase",
  async ({ id }, { rejectWithValue }) => {
    try {
      console.log("eventMarkAsBilledSupabase: Marking client as billed", id);

      // Update the client's last_utilized_at timestamp
      const { data, error } = await supabase
        .from("clients")
        .update({
          last_utilized_at: moment().toISOString(),
          updated_at: moment().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("eventMarkAsBilledSupabase: Supabase error", error);
        throw error;
      }

      console.log(
        "eventMarkAsBilledSupabase: Client marked as billed successfully:",
        data
      );
      return data;
    } catch (error) {
      console.error("eventMarkAsBilledSupabase: Caught error", error);
      return rejectWithValue(error.message);
    }
  }
);
// For Review
export const eventMarkAsBilled = createAsyncThunk(
  "data/eventMarkAsBilled",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/clients/${id}/mark`, {
        billed: "no",
        type: "outstanding",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchOneSupabaseClient = createAsyncThunk(
  "data/fetchOneSupabaseClient",
  async ({ id }, { getState, rejectWithValue }) => {
    try {
      console.log("fetchOneSupabaseClient: Fetching client", id);

      // Get current user from Supabase auth
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        console.error(
          "fetchOneSupabaseClient: Authentication error",
          authError
        );
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .eq("id", id)
        .eq("uid", user.id)
        .single();

      if (error) {
        console.error("fetchOneSupabaseClient: Supabase error", error);
        throw error;
      }

      console.log("fetchOneSupabaseClient: Client fetched successfully:", data);
      return data;
    } catch (error) {
      console.error("fetchOneSupabaseClient: Caught error", error);
      if (error.code === "PGRST116") {
        return rejectWithValue({
          status: 404,
          message: "Client not found",
        });
      }
      return rejectWithValue({
        status: 500,
        message: error.message || "Network error",
      });
    }
  }
);

export const exportAllClients = createAsyncThunk(
  "data/exportAllClients",
  async ({ format }, { rejectWithValue }) => {
    try {
      console.log("exportAllClients: Initiating export with format", format);

      const response = await axiosInstance.post("/clients/dump", {
        format: format,
      });

      console.log("exportAllClients: Export initiated successfully");
      return response.data;
    } catch (error) {
      console.error("exportAllClients: Error initiating export", error);
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to initiate export"
      );
    }
  }
);

export const exportClientEvents = createAsyncThunk(
  "data/exportClientEvents",
  async ({ id, format }, { rejectWithValue }) => {
    try {
      console.log("exportClientEvents: Initiating export with format", format, "for client", id);

      const response = await axiosInstance.post(`/clients/${id}/events/export`, {
        format: format,
      });

      console.log("exportClientEvents: Export initiated successfully");
      return response.data;
    } catch (error) {
      console.error("exportClientEvents: Error initiating export", error);
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to initiate export"
      );
    }
  }
);

// Fetch client events for the events view
export const fetchSupabaseClientEvents = createAsyncThunk(
  "data/fetchSupabaseClientEvents",
  async ({ id }, { getState, rejectWithValue }) => {
    try {
      // First, get the client to retrieve the project prefix
      const client = getState().clientReducer.client;
      
      // If we don't have the client yet, fetch it first
      let projectPrefix = client?.project;
      if (!projectPrefix && id) {
        // Fetch client to get project prefix
        const { data: clientData, error: clientError } = await supabase
          .from("clients")
          .select("project")
          .eq("id", id)
          .single();
        
        if (clientError) {
          throw clientError;
        }
        projectPrefix = clientData?.project;
      }

      // /events defaults from/to to *current day only* when omitted — too narrow for a client list.
      const rangeFrom = moment().subtract(24, "months").startOf("day");
      const rangeTo = moment().add(6, "months").endOf("day");

      const response = await axiosInstance.get("/events", {
        params: {
          id,
          from: toRFC3339WithOffset(rangeFrom),
          to: toRFC3339WithOffset(rangeTo),
          ...(projectPrefix && { prefix: projectPrefix }),
        },
      });
      
      console.log("fetchSupabaseClientEvents: Response:", response);
      
      // The response might have events directly or in a data property
      const events = response.data?.events || response.data || [];
      return Array.isArray(events) ? events : [];
    } catch (error) {
      console.error("fetchSupabaseClientEvents: Error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ------------------------------------------------------------------------
// Slices

export const ClientSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    // Realtime subscription actions
    addClient: (state, action) => {
      // Check if client already exists (prevent duplicates from realtime events)
      const existingIndex = state.clients.findIndex(
        (client) => client.id === action.payload.id
      );
      if (existingIndex === -1) {
        // Add to array (maintain order from fetch)
        state.clients.push(action.payload);
      }
    },
    updateClient: (state, action) => {
      if (!action.payload || !action.payload.id) {
        console.warn('updateClient: Invalid payload, missing id', action.payload);
        return;
      }

      const clientId = action.payload.id;
      const index = state.clients.findIndex(
        (client) => client.id === clientId
      );
      
      if (index !== -1) {
        // Update existing client in the list
        state.clients[index] = { ...state.clients[index], ...action.payload };
        console.log('updateClient: Updated client in list:', clientId);
      } else {
        // Client not in list, add it (could be from realtime update)
        console.log('updateClient: Client not in list, adding:', clientId);
        state.clients.push(action.payload);
      }
      
      // Also update state.client if it's the current client being viewed
      if (state.client && state.client.id === clientId) {
        state.client = { ...state.client, ...action.payload };
        console.log('updateClient: Updated current client being viewed:', clientId);
      }
    },
    removeClient: (state, action) => {
      // Remove from clients array
      state.clients = state.clients.filter(
        (client) => client.id !== action.payload.id
      );
      
      // Clear state.client if it's the one being deleted
      if (state.client && state.client.id === action.payload.id) {
        state.client = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupabaseClients.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSupabaseClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload;
        state.clientsLoaded = true;
      })
      .addCase(fetchSupabaseClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ------------------------------------------------------------
      .addCase(fetchOneSupabaseClient.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOneSupabaseClient.fulfilled, (state, action) => {
        state.loading = false;
        state.client = action.payload;
      })
      .addCase(fetchOneSupabaseClient.rejected, (state, action) => {
        state.loading = false;
        state.client = null;
        state.error = action.error.message;
      })
      // ----------------------------------------------------------------
      .addCase(eventMarkAsBilled.pending, (state) => {
        state.loading = true;
      })
      .addCase(eventMarkAsBilled.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(eventMarkAsBilled.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ----------------------------------------------------------------
      .addCase(eventMarkAsBilledSupabase.pending, (state) => {
        state.loading = true;
      })
      .addCase(eventMarkAsBilledSupabase.fulfilled, (state, action) => {
        state.loading = false;
        // Update the client in the list if it exists
        const index = state.clients.findIndex(
          (client) => client.id === action.payload.id
        );
        if (index !== -1) {
          state.clients[index] = Object.assign(
            {},
            state.clients[index],
            action.payload
          );
        }
      })
      .addCase(eventMarkAsBilledSupabase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ----------------------------------------------------------------
      // Handle create Client
      .addCase(createSupabaseClient.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSupabaseClient.fulfilled, (state, action) => {
        state.loading = false;
        state.clients.push(action.payload);
        state.clientsLoaded = true;
      })
      .addCase(createSupabaseClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ----------------------------------------------------------------
      .addCase(deleteSupabaseClient.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSupabaseClient.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = state.clients.filter(
          (client) => client.id !== action.payload
        );
        state.clientsLoaded = true;
      })
      .addCase(deleteSupabaseClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ----------------------------------------------------------------
      .addCase(updateSupabaseClient.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSupabaseClient.fulfilled, (state, action) => {
        const index = state.clients.findIndex(
          (client) => client.id === action.payload.id
        );
        if (index !== -1) {
          state.clients[index] = Object.assign(
            {},
            state.clients[index],
            action.payload
          );
        }
        state.loading = false;
      })
      .addCase(updateSupabaseClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ----------------------------------------------------------------
      .addCase(exportAllClients.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportAllClients.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportAllClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ----------------------------------------------------------------
      .addCase(exportClientEvents.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportClientEvents.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportClientEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ----------------------------------------------------------------
      .addCase(fetchSupabaseClientEvents.pending, (state) => {
        state.clientEventsLoading = true;
      })
      .addCase(fetchSupabaseClientEvents.fulfilled, (state, action) => {
        state.clientEventsLoading = false;
        state.clientEvents = action.payload;
      })
      .addCase(fetchSupabaseClientEvents.rejected, (state, action) => {
        state.clientEventsLoading = false;
        state.error = action.payload;
      })
      // Keep client event rows in sync when an event is updated via Calendar API
      // (avoids refetching the whole list and global loading flicker).
      .addCase(updateEvent.fulfilled, (state, action) => {
        const payload = action.payload;
        if (!payload?.id) return;
        const idx = state.clientEvents.findIndex((e) => e.id === payload.id);
        if (idx === -1) return;
        state.clientEvents[idx] = {
          ...state.clientEvents[idx],
          ...payload,
        };
      });
  },
});

// Export actions and reducers
export const { 
  setLoading, 
  setError,
  addClient,
  updateClient,
  removeClient
} = ClientSlice.actions;

export default ClientSlice.reducer;
